import React, { useState, useEffect } from 'react';
import {
  Create,
  Visibility,
  Work,
  Done,
  PendingActions,
  TrendingUp,
  TrendingDown,
  AttachMoney,
  Person,
  Assignment,
  CheckCircle,
  Error,
  Warning,
  Info,
  CalendarToday,
  AccessTime,
  Star,
  StarBorder,
     Refresh,
   Download,
   Upload,
   Add,
  Schedule,
  TrendingFlat,
  Speed,
  Assessment,
  BarChart,
  PieChart,
  Timeline,
     Settings,
   Help,
  Dashboard as DashboardIcon,
  Phone,
  Mail,
  Business
} from '@mui/icons-material';
import { Calendar, MapPin, Shield, LogOut, X, Filter } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { designerDesignCaseApi } from '../../services/designer/designCaseApi';
import { useAuth } from '../../contexts/AuthContext';
import CaseDetailModal from './CaseDetailModal';

const DesignerDashboard = () => {
  const { user, logout } = useAuth();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showCaseDetailModal, setShowCaseDetailModal] = useState(false);
  const [selectedCase, setSelectedCase] = useState(null);
  const [dateFilter, setDateFilter] = useState('all');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [showCustomDateRange, setShowCustomDateRange] = useState(false);
  const [stats, setStats] = useState({
    totalCases: 0,
    pendingCases: 0,
    inProgressCases: 0,
    completedCases: 0,
    totalEarnings: 0,
    averageRating: 0,
    completionRate: 0,
    onTimeDelivery: 0
  });
  const [recentCases, setRecentCases] = useState([]);
  const [upcomingDeadlines, setUpcomingDeadlines] = useState([]);
  const [performanceData, setPerformanceData] = useState({
    monthlyEarnings: [],
    caseCompletion: [],
    ratings: []
  });

  useEffect(() => {
    fetchDashboardData();
  }, [dateFilter, customStartDate, customEndDate]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const token = sessionStorage.getItem('token');
      
      if (!token) {
        toast.error('Vui lòng đăng nhập lại');
        return;
      }

      // Fetch designer cases
      const response = await designerDesignCaseApi.getMyCases(token);
      const allCases = response.data || [];
      console.log('All cases:', allCases);
      console.log('Cases by status:', {
        pending: allCases.filter(c => c.status === 'pending').length,
        in_progress: allCases.filter(c => c.status === 'in_progress').length,
        approved: allCases.filter(c => c.status === 'approved').length,
        rejected: allCases.filter(c => c.status === 'rejected').length
      });
      
      // Filter cases based on date filter
      const filteredCases = filterCasesByDate(allCases);
      setCases(filteredCases);

      // Calculate stats
      const stats = calculateStats(filteredCases);
      setStats(stats);

      // Get recent cases (last 5) - chỉ lấy cases đã hoàn thành gần nhất
      const recentCompletedCases = filteredCases
        .filter(case_ => case_.status === 'approved')
        .sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at))
        .slice(0, 5);
      setRecentCases(recentCompletedCases);

      // Get upcoming deadlines for cases in progress (last 5) - chỉ lấy 5 cases đang làm gần nhất
      const inProgressCases = allCases
        .filter(case_ => case_.status === 'in_progress')
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, 5);
      
      console.log('Cases đang làm:', inProgressCases.length);
      setUpcomingDeadlines(inProgressCases);

      // Mock performance data
      setPerformanceData({
        monthlyEarnings: [
          { month: 'T1', earnings: 12000000 },
          { month: 'T2', earnings: 15000000 },
          { month: 'T3', earnings: 18000000 },
          { month: 'T4', earnings: 14000000 },
          { month: 'T5', earnings: 20000000 },
          { month: 'T6', earnings: 22000000 }
        ],
        caseCompletion: [
          { month: 'T1', completed: 8 },
          { month: 'T2', completed: 12 },
          { month: 'T3', completed: 15 },
          { month: 'T4', completed: 10 },
          { month: 'T5', completed: 18 },
          { month: 'T6', completed: 20 }
        ],
        ratings: [
          { month: 'T1', rating: 4.2 },
          { month: 'T2', rating: 4.5 },
          { month: 'T3', rating: 4.3 },
          { month: 'T4', rating: 4.6 },
          { month: 'T5', rating: 4.4 },
          { month: 'T6', rating: 4.7 }
        ]
      });

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Có lỗi xảy ra khi tải dữ liệu dashboard');
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (cases) => {
    const totalCases = cases.length;
    const pendingCases = cases.filter(c => c.status === 'pending').length;
    const inProgressCases = cases.filter(c => c.status === 'in_progress').length;
    const completedCases = cases.filter(c => c.status === 'approved').length;
    
    // Tính tổng thu nhập từ các đơn đã hoàn thành
    const totalEarnings = cases
      .filter(c => c.status === 'approved')
      .reduce((sum, c) => sum + (c.cost_per_design || 0), 0);
    
    const averageRating = cases.length > 0 
      ? cases.reduce((sum, c) => sum + (c.rating || 0), 0) / cases.length 
      : 0;
    
    const completionRate = totalCases > 0 ? (completedCases / totalCases) * 100 : 0;
    
    // Tính tỷ lệ giao hàng đúng hạn
    const onTimeDeliveries = cases.filter(c => {
      if (c.status !== 'approved') return false;
      const deadline = new Date(c.deadline);
      const completedAt = new Date(c.completed_at || c.updated_at);
      return completedAt <= deadline;
    }).length;
    
    const onTimeDelivery = completedCases > 0 ? (onTimeDeliveries / completedCases) * 100 : 0;

    return {
      totalCases,
      pendingCases,
      inProgressCases,
      completedCases,
      totalEarnings,
      averageRating,
      completionRate,
      onTimeDelivery
    };
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'text-yellow-600 bg-yellow-50';
      case 'in_progress': return 'text-blue-600 bg-blue-50';
      case 'approved': return 'text-green-600 bg-green-50';
      case 'rejected': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <PendingActions className="w-4 h-4" />;
      case 'in_progress': return <Work className="w-4 h-4" />;
      case 'approved': return <Done className="w-4 h-4" />;
      case 'rejected': return <Error className="w-4 h-4" />;
      default: return <Info className="w-4 h-4" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending': return 'Chờ duyệt';
      case 'in_progress': return 'Đang làm';
      case 'approved': return 'Hoàn thành';
      case 'rejected': return 'Từ chối';
      default: return 'Không xác định';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('vi-VN');
  };

  const formatProductName = (productName) => {
    if (!productName) return 'N/A';
    // Xóa phần SKU khỏi tên sản phẩm
    return productName.replace(/\s*SKU.*$/i, '');
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('vi-VN');
  };

  const getDaysUntilDeadline = (deadline) => {
    const today = new Date();
    const deadlineDate = new Date(deadline);
    const diffTime = deadlineDate.getTime() - today.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const getDeadlineColor = (deadline) => {
    const days = getDaysUntilDeadline(deadline);
    if (days < 0) return 'text-red-600';
    if (days <= 1) return 'text-orange-600';
    if (days <= 3) return 'text-yellow-600';
    return 'text-green-600';
  };

  const filterCasesByDate = (cases) => {
    if (dateFilter === 'all') return cases;
    
    const now = new Date();
    let startDate, endDate;
    
    switch (dateFilter) {
      case 'today':
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
        break;
      case 'yesterday':
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
        endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        break;
      case 'thisWeek':
        const dayOfWeek = now.getDay();
        const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - daysToSubtract);
        endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
        break;
      case 'thisMonth':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        endDate = new Date(now.getFullYear(), now.getMonth() + 1, 1);
        break;
      case 'lastMonth':
        startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        endDate = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case 'custom':
        if (!customStartDate || !customEndDate) return cases;
        startDate = new Date(customStartDate);
        endDate = new Date(customEndDate);
        endDate.setDate(endDate.getDate() + 1); // Include end date
        break;
      default:
        return cases;
    }
    
    return cases.filter(case_ => {
      const caseDate = new Date(case_.created_at);
      return caseDate >= startDate && caseDate < endDate;
    });
  };

  const handleLogout = () => {
    logout();
    toast.success('Đăng xuất thành công');
  };

  const handleCaseClick = (case_) => {
    setSelectedCase(case_);
    setShowCaseDetailModal(true);
  };

  const closeCaseDetailModal = () => {
    setShowCaseDetailModal(false);
    setSelectedCase(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Đang tải dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 sm:p-6 lg:p-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-7xl mx-auto"
      >
        {/* Header with Profile Link */}
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-6 lg:mb-8 space-y-4 lg:space-y-0">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-2">Dashboard Designer</h1>
            <p className="text-sm lg:text-base text-gray-600">Quản lý và theo dõi các dự án thiết kế</p>
          </div>
          
          {/* Date Filter */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-4 w-full lg:w-auto">
            <div className="flex items-center space-x-2">
              <Filter className="w-4 h-4 text-gray-600" />
              <select
                value={dateFilter}
                onChange={(e) => {
                  setDateFilter(e.target.value);
                  if (e.target.value !== 'custom') {
                    setShowCustomDateRange(false);
                  } else {
                    setShowCustomDateRange(true);
                  }
                }}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">Tất cả thời gian</option>
                <option value="today">Hôm nay</option>
                <option value="yesterday">Hôm qua</option>
                <option value="thisWeek">Tuần này</option>
                <option value="thisMonth">Tháng này</option>
                <option value="lastMonth">Tháng trước</option>
                <option value="custom">Tùy chỉnh</option>
              </select>
            </div>
            
            {showCustomDateRange && (
              <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-2">
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <span className="text-gray-500 text-sm">đến</span>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            )}
            
            <button
              onClick={fetchDashboardData}
              className="flex items-center justify-center space-x-2 px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
            >
              <Refresh className="w-4 h-4" />
              <span className="hidden sm:inline text-sm">Làm mới</span>
            </button>
          </div>
        </div>

         {/* Stats Cards */}
         <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-6 lg:mb-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            whileHover={{ scale: 1.05 }}
            className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl lg:rounded-2xl p-4 sm:p-6 text-white shadow-xl"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm font-medium">Tổng Cases</p>
                <p className="text-2xl sm:text-3xl font-bold">{stats.totalCases}</p>
                <p className="text-blue-200 text-xs mt-1">Tất cả thời gian</p>
              </div>
              <Assignment className="text-2xl sm:text-4xl text-blue-200" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            whileHover={{ scale: 1.05 }}
            className="bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl lg:rounded-2xl p-4 sm:p-6 text-white shadow-xl"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm font-medium">Đã hoàn thành</p>
                <p className="text-2xl sm:text-3xl font-bold">{stats.completedCases}</p>
                <p className="text-green-200 text-xs mt-1">{stats.completionRate}% tỷ lệ</p>
              </div>
              <Done className="text-2xl sm:text-4xl text-green-200" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            whileHover={{ scale: 1.05 }}
            className="bg-gradient-to-br from-yellow-500 to-orange-500 rounded-xl lg:rounded-2xl p-4 sm:p-6 text-white shadow-xl"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-yellow-100 text-sm font-medium">Đang làm</p>
                <p className="text-2xl sm:text-3xl font-bold">{stats.inProgressCases}</p>
                <p className="text-yellow-200 text-xs mt-1">Cases hiện tại</p>
              </div>
                             <Business className="text-2xl sm:text-4xl text-yellow-200" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            whileHover={{ scale: 1.05 }}
            className="bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl p-4 sm:p-6 text-white shadow-xl"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm font-medium">Tổng thu nhập</p>
                <p className="text-xl sm:text-2xl font-bold">{formatCurrency(stats.totalEarnings)}</p>
                <p className="text-purple-200 text-xs mt-1">Tất cả thời gian</p>
              </div>
              <AttachMoney className="text-2xl sm:text-4xl text-purple-200" />
            </div>
          </motion.div>
        </div>

        {/* Performance Metrics */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Rating & Performance */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="bg-white rounded-2xl shadow-xl p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-800">Đánh giá & Hiệu suất</h3>
              <Star className="text-yellow-500" />
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Đánh giá trung bình</span>
                <div className="flex items-center space-x-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star key={star} className={`w-4 h-4 ${star <= stats.averageRating ? 'text-yellow-500' : 'text-gray-300'}`} />
                  ))}
                  <span className="ml-2 text-sm font-semibold">{stats.averageRating}/5</span>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Tỷ lệ hoàn thành</span>
                <span className="text-green-600 font-semibold">{stats.completionRate}%</span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Giao đúng hạn</span>
                <span className="text-blue-600 font-semibold">{stats.onTimeDelivery}%</span>
              </div>
            </div>
          </motion.div>

          {/* Recent Cases */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="bg-white rounded-2xl shadow-xl p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-800">Cases gần đây</h3>
              <Assignment className="text-blue-500" />
            </div>
            
                         <div className="space-y-3">
               {recentCases.map((case_, index) => (
                 <div 
                   key={case_.id} 
                   className="flex items-center justify-between p-3 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 transition-colors"
                   onClick={() => handleCaseClick(case_)}
                 >
                   <div className="flex items-center space-x-3">
                     <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                       <span className="text-white text-xs font-bold">{case_.id}</span>
                     </div>
                                          <div>
                        <p className="text-sm font-medium text-gray-800">
                          {formatProductName(case_.product_name) || `Case #${case_.id}`}
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatDate(case_.created_at)}
                        </p>
                      </div>
                   </div>
                   <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(case_.status)}`}>
                     {getStatusIcon(case_.status)}
                     <span className="ml-1">{getStatusText(case_.status)}</span>
                   </span>
                 </div>
               ))}
              
              {recentCases.length === 0 && (
                <div className="text-center py-4">
                  <Assignment className="text-gray-300 text-2xl mx-auto mb-2" />
                  <p className="text-gray-500 text-sm">Chưa có cases nào</p>
                </div>
              )}
            </div>
          </motion.div>

          {/* Upcoming Deadlines */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.7 }}
            className="bg-white rounded-2xl shadow-xl p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-800">Deadlines đang làm</h3>
              <Schedule className="text-red-500" />
            </div>
            
                         <div className="space-y-3">
               {upcomingDeadlines.map((case_, index) => {
                 const daysUntil = getDaysUntilDeadline(case_.deadline);
                 return (
                   <div 
                     key={case_.id} 
                     className="p-3 bg-red-50 border border-red-200 rounded-xl cursor-pointer hover:bg-red-100 transition-colors"
                     onClick={() => handleCaseClick(case_)}
                   >
                                          <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-800">
                          {formatProductName(case_.product_name) || `Case #${case_.id}`}
                        </span>
                        <span className={`text-xs font-semibold ${getDeadlineColor(case_.deadline)}`}>
                          {daysUntil < 0 ? 'Quá hạn' : `${daysUntil} ngày`}
                        </span>
                      </div>
                     <div className="flex items-center justify-between text-xs text-gray-500">
                       <span>Deadline: {formatDateTime(case_.deadline)}</span>
                       <span className={`font-semibold ${getDeadlineColor(case_.deadline)}`}>
                         {formatCurrency(case_.cost_per_design || 0)}
                       </span>
                     </div>
                   </div>
                 );
               })}
              
              {upcomingDeadlines.length === 0 && (
                <div className="text-center py-4">
                  <Schedule className="text-gray-300 text-2xl mx-auto mb-2" />
                  <p className="text-gray-500 text-sm">Không có cases đang làm</p>
                </div>
              )}
            </div>
          </motion.div>
        </div>

        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="bg-white rounded-2xl shadow-xl p-6 mb-8"
        >
          <h3 className="text-lg font-bold text-gray-800 mb-4">Thao tác nhanh</h3>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="flex items-center space-x-3 p-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl hover:shadow-lg transition-all duration-300"
            >
                               <Business className="text-xl" />
              <span className="font-medium">Xem cases</span>
            </motion.button>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="flex items-center space-x-3 p-4 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl hover:shadow-lg transition-all duration-300"
            >
              <Upload className="text-xl" />
              <span className="font-medium">Upload design</span>
            </motion.button>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="flex items-center space-x-3 p-4 bg-gradient-to-r from-yellow-500 to-orange-600 text-white rounded-xl hover:shadow-lg transition-all duration-300"
            >
              <Assessment className="text-xl" />
              <span className="font-medium">Báo cáo</span>
            </motion.button>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="flex items-center space-x-3 p-4 bg-gradient-to-r from-purple-500 to-pink-600 text-white rounded-xl hover:shadow-lg transition-all duration-300"
            >
              <Settings className="text-xl" />
              <span className="font-medium">Cài đặt</span>
            </motion.button>
          </div>
        </motion.div>

        {/* Monthly Performance Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.9 }}
          className="bg-white rounded-2xl shadow-xl p-6"
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-bold text-gray-800">Hiệu suất tháng này</h3>
            <div className="flex items-center space-x-2">
              <TrendingUp className="text-green-500" />
              <span className="text-sm text-green-600 font-medium">+12% so với tháng trước</span>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-xl font-bold">{stats.inProgressCases}</span>
              </div>
              <p className="text-sm text-gray-600">Cases đang làm</p>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-xl font-bold">{stats.completedCases}</span>
              </div>
              <p className="text-sm text-gray-600">Cases hoàn thành</p>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-yellow-500 to-orange-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-xl font-bold">{formatCurrency(stats.totalEarnings)}</span>
              </div>
              <p className="text-sm text-gray-600">Thu nhập</p>
            </div>
          </div>
        </motion.div>
      </motion.div>

                           {/* Case Detail Modal */}
              <CaseDetailModal
                isOpen={showCaseDetailModal}
                onClose={closeCaseDetailModal}
                caseData={selectedCase}
              />

       {/* Profile Modal */}
       <AnimatePresence>
         {showProfileModal && (
          <motion.div
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -50 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
            onClick={() => setShowProfileModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-gray-900">Thông tin cá nhân</h3>
                                 <button onClick={() => setShowProfileModal(false)} className="text-gray-500 hover:text-gray-700">
                   <X className="w-6 h-6" />
                 </button>
              </div>

              {/* Avatar Section */}
              <div className="text-center mb-6">
                <div className="relative inline-block">
                  <div className="w-24 h-24 sm:w-32 sm:h-32 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full mx-auto mb-4 flex items-center justify-center border-4 border-gray-100 shadow-lg">
                    <span className="text-white font-bold text-2xl sm:text-3xl">
                      {user?.name?.charAt(0)?.toUpperCase() || 'D'}
                    </span>
                  </div>
                </div>
                
                {/* Tên hiển thị dưới ảnh */}
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">
                  {user?.name || 'Designer Name'}
                </h2>
                
                {/* Ngày tham gia và vai trò trên cùng một hàng */}
                <div className="flex flex-col sm:flex-row justify-center items-center space-y-2 sm:space-y-0 sm:space-x-6 text-sm text-gray-600">
                  <div className="flex items-center space-x-2">
                    <Calendar className="w-4 h-4" />
                    <span>
                      {user?.createdAt ? new Date(user.createdAt).toLocaleDateString('vi-VN') : 'Chưa có thông tin'}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Shield className="w-4 h-4" />
                    <span>Thiết kế</span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left Column - Basic Info */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email
                    </label>
                    <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                      <Mail className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-900">{user?.email || 'Chưa có thông tin'}</span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Số điện thoại
                    </label>
                    <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                      <Phone className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-900">{user?.phone || 'Chưa có thông tin'}</span>
                    </div>
                  </div>
                </div>

                {/* Right Column - Additional Info */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Họ và tên
                    </label>
                    <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                      <Person className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-900">{user?.name || 'Chưa có thông tin'}</span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Ngày sinh
                    </label>
                    <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-900">
                        {user?.birthDate ? new Date(user.birthDate).toLocaleDateString('vi-VN') : 'Chưa có thông tin'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Địa chỉ - Full width ở dưới cùng */}
              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Địa chỉ
                </label>
                <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                  <MapPin className="w-4 h-4 text-gray-400 mt-0.5" />
                  <span className="text-gray-900">{user?.address || 'Chưa có thông tin'}</span>
                </div>
              </div>

              {/* Logout Button */}
              <div className="mt-6">
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors duration-200"
                >
                  <LogOut className="w-5 h-5" />
                  <span>Đăng xuất</span>
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DesignerDashboard; 